import React, { Component } from "react";

/**
 * Code-split a component without React.lazy/Suspense. This project pairs the
 * React 18 package with ReactDOM 16, and ReactDOM 16 cannot resolve React 18
 * lazy objects — a plain dynamic import() inside a class component works with
 * any combination.
 *
 * loadable(() => import("./Page"), Fallback) returns a component that renders
 * Fallback until the chunk arrives. `.preload()` starts the download early.
 */
export default function loadable(importer, Fallback = null) {
  let Loaded = null;
  let promise = null;

  const load = () => {
    if (!promise) {
      promise = importer().then(
        (module) => {
          Loaded = module.default || module;
          return Loaded;
        },
        (error) => {
          promise = null;
          throw error;
        }
      );
    }
    return promise;
  };

  class Loadable extends Component {
    state = { Comp: Loaded, failed: false };

    componentDidMount() {
      if (!this.state.Comp) this.fetch();
    }

    componentWillUnmount() {
      this.unmounted = true;
    }

    fetch = () => {
      this.setState({ failed: false });
      load().then(
        (Comp) => !this.unmounted && this.setState({ Comp }),
        () => !this.unmounted && this.setState({ failed: true })
      );
    };

    render() {
      const { Comp, failed } = this.state;
      if (Comp) return <Comp {...this.props} />;
      if (failed) {
        // Usually a stale chunk after a new deploy, or a dropped connection.
        return (
          <div className="v-state v-state--compact" role="alert">
            <h2 className="v-state__title">This page didn't load</h2>
            <p className="v-state__desc">Check your connection, then reload to get the latest version.</p>
            <div className="v-state__actions">
              <button type="button" className="v-btn v-btn--primary" onClick={() => window.location.reload()}>
                Reload page
              </button>
            </div>
          </div>
        );
      }
      return Fallback ? <Fallback /> : null;
    }
  }

  Loadable.preload = load;
  return Loadable;
}
